export interface GenericComplianceDataRequest {
  companyId: string;
  complianceStandard: ComplianceStandard;
}

export interface GenerateReportRequest extends GenericComplianceDataRequest {
  // TODO: Make a generic filter
  filters?: GetOverviewDataRequestFilters;
}

export interface InitComplianceRequest {
  locale: string;
}

export interface MultipleSelectEnumFilter {
  in: Array<number>;
}

export interface MultipleSelectFilter {
  in: Array<string>;
}

export interface RangeFilter {
  min: number;
  max: number;
}

export interface GetOverviewDataRequestFilters {
  checkResult: MultipleSelectEnumFilter;
  sectionId: MultipleSelectFilter;
  category: MultipleSelectFilter;
  riskScore: string;
  passedPercentage?: Array<RangeFilter>;
  includeNotEvaluated?: boolean;
}

export interface PaginationRequest {
  maxItems: number;
  pageNumber: number;
}

export interface GetOverviewDataRequest extends GenericComplianceDataRequest {
  pagination: PaginationRequest;
  filters?: GetOverviewDataRequestFilters;
}

export interface ChecksResults {
  ignored: number;
  compliant: number;
  nonCompliant: number;
}

export interface CheckStat {
  totalChecks: number;
  checksResults: ChecksResults;
  passedPercentage?: number | null;
}

export interface ChecksStats {
  overAllStats: CheckStat;
  filteredStats?: CheckStat;
}

export interface PaginationResponse {
  totalItems: number;
  items: number;
  pageNumber: number;
}

export interface ControlDetails {
  id: string;
  name: string;
  subId: string;
  controlResult: string;
  description: string;
  checksStats: CheckStat;
}

export interface SectionDetails {
  id: string;
  name: string;
}

export interface FindingIndicatorIds {
  indicatorIds: number[];
}

export interface Findings {
  misconfiguration: FindingIndicatorIds;
  userBehaviorRisk: FindingIndicatorIds;
}

export interface ControlsOverview {
  controlDetails: ControlDetails;
  sectionDetails: SectionDetails;

  findings: Findings;
  category: string;
}

export interface TotalEndpoints {
  totalEndpoints: number;
}

export interface GetOverviewDataResponse {
  checksStats: ChecksStats;
  controlsOverview: ControlsOverview[];
  pagination: PaginationResponse;
  totalEndpoints: number;
}

export interface GetDashboardDataResponse {
  checksStats: ChecksStats;
}

export interface GenerateReportResponse {
  file: Buffer;
  mime: string;
  name: string;
  empty?: boolean;
}

export interface GetReportDataResponse {
  data: string;
}

export enum Column {
  // Although `COMPLIANCE` is not shown by default, it is useful to have this
  // to provide dynamic filter options
  COMPLIANCE = 0,
  CONTROL = 1,
  CONTROL_ID = 2,
  CATEGORY = 3,
  SECTION = 4,
  SECTION_ID = 5,
  SCORING = 6,
}

export enum ColumnKeys {
  COMPLIANCE = 'control',
  CONTROL = 'control',
  CONTROL_ID = 'control_id',
  CATEGORY = 'category',
  SECTION = 'section',
  SECTION_ID = 'section_id',
  SCORING = 'scoring',
}

// Please check the documentation for more details:
// https://bitdefender.atlassian.net/wiki/spaces/GZDOCS/pages/1153469016/The+Grid+Web+Component#Grid-columns-configurable-properties
export enum ColumnType {
  DATE = 0,
  ENUM = 1,
  STRING = 2,
  NUMBER = 3,
  ACTION = 4,
  BOOL = 5,
  COMPANY = 6,
  SELECT = 7,
  MULTIPLE_VALUE = 8,
  RANGE = 9,
  CUSTOM = 10,
}

export interface ColumnValue {
  label: string;
  // Although other components support `int`, we should try to use `string` to be more pedantic
  value: string;
}

// TODO: Should we use the same `ColumnDetailsBase` from Alerts?
export interface ColumnDetailsBase {
  column: Column;
  key: ColumnKeys;
  columnName: string;
  type: ColumnType;
  values?: Array<ColumnValue>;
  editingDisabled?: boolean;
}

export interface ColumnDetails extends ColumnDetailsBase {
  removable: boolean;
  canCopyContent?: boolean;
}

export interface InitComplianceResponse {
  // views: Array<ViewConfiguration>;
  allColumns: Array<ColumnDetails>;
}

export enum ComplianceStandard {
  BDCHBWINDOWS = 'bdchb-windows',
  CIS_V8 = 'CIS_v8',
  CMMC2 = 'cmmc-2',
  DORA = 'DORA',
  GDPR = 'GDPR',
  HIPAA = 'hipaa',
  ISO27001 = 'Iso27001',
  // TODO: Recheck these values, Keep in mind this Enum is used for Compliance Report
  // TODO: Recheck NIS2, value is not yet available anywhere
  NIS2 = 'NIS2',
  PCIDSSV4 = 'pci-dss-v4',
  SOC2 = 'SOC2',
}

export const ComplianceStandardTranslations = {
  // TODO: Recheck with the product team on what exactly should we use to show the users.
  // Populate with other Compliance as well.
  [ComplianceStandard.BDCHBWINDOWS]: 'Cyber Hygiene - Windows',
  [ComplianceStandard.CIS_V8]: 'CIS v8.0',
  [ComplianceStandard.GDPR]: 'GDPR (EU)',
  [ComplianceStandard.ISO27001]: 'ISO/IEC 27001:2022',
  [ComplianceStandard.SOC2]: 'SOC 2',
  [ComplianceStandard.NIS2]: 'NIS2 Directive (EU)',
  [ComplianceStandard.DORA]: 'DORA (EU)',
  [ComplianceStandard.CMMC2]: 'CMMC 2.0 (US)',
  [ComplianceStandard.PCIDSSV4]: 'PCI DSS V4.0.1',
  [ComplianceStandard.HIPAA]: 'HIPAA (US)',
};

export enum Severity {
  NONE = 'none',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum OperatingSystem {
  IOS = 'IOS',
  ANDROID = 'android',
  WINDOWS = 'windows',
  LINUX = 'linux',
  SOLARIS = 'solaris',
  MACOSX = 'macosx',
  CONTAINER = 'container',
}

export enum UserType {
  LOCAL = 'local',
  AD = 'ad',
  AAD = 'aad',
}

export const OperatingSystemTranslations = {
  [OperatingSystem.IOS]: 'IOS',
  [OperatingSystem.ANDROID]: 'Android',
  [OperatingSystem.WINDOWS]: 'Windows',
  [OperatingSystem.LINUX]: 'Linux',
  [OperatingSystem.SOLARIS]: 'Solaris',
  [OperatingSystem.MACOSX]: 'Mac OS X',
  [OperatingSystem.CONTAINER]: 'Container',
};

export enum CheckStatus {
  PASSED = 'passed',
  FAILED = 'failed',
  SUPPRESSED = 'suppressed',
}

export enum ControlStatus {
  PASSED = 0,
  FAILED = 1,
  NOT_EVALUATED = 2,
}

export const ControlCheckStatus = {
  [ControlStatus.FAILED]: CheckStatus.FAILED,
  [ControlStatus.PASSED]: CheckStatus.PASSED,
  [ControlStatus.NOT_EVALUATED]: CheckStatus.SUPPRESSED,
};

export const ControlStatusTranslations = {
  [ControlStatus.FAILED]: 'fail',
  [ControlStatus.PASSED]: 'pass',
  [ControlStatus.NOT_EVALUATED]: 'not evaluated',
};

export enum AssetType {
  ENDPOINTS = 'endpoints',
  USERS = 'users',
}

export const AssetTypeTranslations = {
  [AssetType.ENDPOINTS]: 'Endpoints',
};

export interface DataProviderOutput {
  stats: OutputStatistics;
  controlOverview: OverviewItems;
}

export interface ChecksStatistics {
  total: number;
  finalScore: number | null;
  passedPercentage: number | null;
  status: {
    passedPercentage: number | null;
    passed: number;
    failed: number;
    suppressed: number;
  };
  severityByStatus?: {
    // CSPM data calculates severity on `pass` findings, thus, this is optional
    [CheckStatus.PASSED]?: SeverityBreakdown;
    // Only failing states have a severity for indicators
    [CheckStatus.FAILED]: SeverityBreakdown;
    [CheckStatus.SUPPRESSED]: SeverityBreakdown;
  };
}

export interface OutputStatistics {
  assets?: AssetsStatistics;
  controls: {
    total: number;
    status: {
      passedPercentage: number | null;
      passed: number;
      failed: number;
      notEvaluated: number;
    };
  };
  checks: ChecksStatistics;
}

export interface AssetsStatistics {
  evaluated: number;
  all: number;
  items: {
    [AssetType: string]: {
      type: AssetType;
      evaluated: number;
      all: number;
      assets: {
        involved: {
          [category: string]: number;
        };
        ignored: {
          [category: string]: number;
        };
      };
    };
  };
}

export interface OverviewItems {
  [id: string]: GroupData | ControlData;
}

export interface GroupData {
  text?: string;
  items: OverviewItems;
}

export interface SeverityBreakdown {
  none: number;
  high: number;
  medium: number;
  low: number;
}

export interface ControlData {
  text?: string;
  name?: string;
  status: ControlStatus;
  statistics: ChecksStatistics;
  indicators: DataProviderOutputIndicatorsMap;
}

export interface DataProviderOutputIndicatorsMap {
  [indicatorId: string]: IndicatorData;
}

// refactor(dhipolito): Remove duplicates
export enum EventState {
  ACTIVE = 1,
  IGNORED = 2,
}

export interface IndicatorData {
  statistics: ChecksStatistics;
  state: EventState;
  severityMeta: {
    rawSeverity: number;
    failedAssetsCount: number;
  };
  text?: string;
  operatingSystem?: Array<string>;
  category?: string;
}

export interface RiskData {
  indicatorId: number;
  name?: string;
  checksStats: CheckStat;
  riskScore: number;
  state: EventState;
}

export interface BaseControlAsset {
  checksStats: CheckStat;
  scoreBreakdown: {
    baseScore: number;
    finalScore: number;
    multiplier: object;
  };
  state: number;
}

export interface ControlIdentities extends BaseControlAsset {
  username: string;
  uniqueIdentifier: string;
  userType: number[];
}

export interface ControlResource extends BaseControlAsset {
  endpointName: string;
  endpointId: string;
  endpointType: number;
}

interface ControlAssetExtraDetails {
  control: {
    operatingSystem?: number[];
    applicableResource?: Array<{ endpointId: string; endpointName: string }>;
  };
}

export interface GetControlAssetsRequest extends GenericComplianceDataRequest {
  filters: {
    indicatorId: {
      in: Array<string | number>;
    };
  };
}

export interface GetControlAssetsResponse<T = ControlResource> {
  data: Array<T>;
  total: number;
  extraDetails?: ControlAssetExtraDetails;
}

export interface GetControlResourcesResponse extends GetControlAssetsResponse<ControlResource> {}
export interface GetControlIdentitiesResponse extends GetControlAssetsResponse<ControlIdentities> {}

export interface GetRisksResponse {
  data: Array<RiskData>;
  total: number;
}

export interface GetRisksRequest extends GetControlAssetsRequest {}

export interface ComplianceControlRowData {
  item: ControlsOverview;
}

export interface IndicatorSeverityMap {
  [indicatorId: string]: Severity;
}

export interface IndicatorMetadataMap {
  [indicatorId: string]: {
    severity: Severity;
    state: EventState;
    score: number;
  };
}

export const ComplianceStandards = {
  [ComplianceStandard.BDCHBWINDOWS]: {
    name: ComplianceStandardTranslations[ComplianceStandard.BDCHBWINDOWS],
    free: true,
  },
  [ComplianceStandard.CIS_V8]: {
    name: ComplianceStandardTranslations[ComplianceStandard.CIS_V8],
    free: false,
  },
  [ComplianceStandard.CMMC2]: {
    name: ComplianceStandardTranslations[ComplianceStandard.CMMC2],
    free: false,
  },
  [ComplianceStandard.DORA]:{
    name: ComplianceStandardTranslations[ComplianceStandard.DORA],
    free: false,
  },
  [ComplianceStandard.GDPR]: {
    name: ComplianceStandardTranslations[ComplianceStandard.GDPR],
    free: false,
  },
  [ComplianceStandard.HIPAA]: {
    name: ComplianceStandardTranslations[ComplianceStandard.HIPAA],
    free: false,
  },
  [ComplianceStandard.ISO27001]: {
    name: ComplianceStandardTranslations[ComplianceStandard.ISO27001],
    free: false,
  },
  [ComplianceStandard.NIS2]: {
    name: ComplianceStandardTranslations[ComplianceStandard.NIS2],
    free: false,
  },
  [ComplianceStandard.PCIDSSV4]: {
    name: ComplianceStandardTranslations[ComplianceStandard.PCIDSSV4],
    free: false,
  },
  [ComplianceStandard.SOC2]: {
    name: ComplianceStandardTranslations[ComplianceStandard.SOC2],
    free: false,
  },
};

export enum COMPLIANCE_LIMIT {
  DESC_ENDPOINT_COUNT_HARD_LIMIT = '100.000',
  ENDPOINT_COUNT_HARD_LIMIT = 100000,
  FREE_TIER_LIMIT = 1000,
}

export const COMPLIANCE_REDIS_TTL = 60 * 60; // 1 hour